import * as React from "react";
import SessionPanel from "../shared/SessionPanel";
import "styles/verify-code.scss";
import { LoginVerificationType } from "models/Constants";

const VerifyCode: React.FC<{
  email?: string;
  phone?: string;
  verifyCodeFeedback?: string;
  imagePath?: string;
  verificationType?: LoginVerificationType;
  secondsRemainingToAllowCodeResend: number;
  isVerifyingCode: boolean;
  onGoBack: () => void;
  onSendCode: (ignoreLastCode?: boolean) => void;
  onVerifyCode: (code: string) => void;
}> = ({
  email,
  phone,
  verifyCodeFeedback,
  imagePath,
  verificationType,
  secondsRemainingToAllowCodeResend,
  isVerifyingCode,
  onGoBack,
  onSendCode,
  onVerifyCode,
}) => {
  const CODE_LENGTH = new Array(6).fill(0);
  const [code, setCode] = React.useState<string>("");
  const values = code.split("");
  const input = React.createRef<HTMLInputElement>();
  const [focused, setFocused] = React.useState(false);
  const selectedIndex =
    values.length < CODE_LENGTH.length ? values.length : CODE_LENGTH.length - 1;

  React.useEffect(() => {
    onSendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (code.length >= CODE_LENGTH.length) onVerifyCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleClick = () => {
    input.current?.focus();
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    if (focused) {
      input.current?.blur();
    }
    setFocused(false);
  };

  const handleChange = (e: any) => {
    const val = e.target.value;

    if (code.length >= CODE_LENGTH.length) {
      return null;
    }

    const combinedValue = (code + val).slice(0, CODE_LENGTH.length);
    setCode(combinedValue);
    return combinedValue;
  };

  const handleKeyUp = (e: any) => {
    if (e.key === "Backspace") {
      const val = code.slice(0, code.length - 1);
      setCode(val);
      return val;
    }
  };

  const hideInput = !(values.length < CODE_LENGTH.length);

  return (
    <>
      <SessionPanel
        image={imagePath}
        isLoading={isVerifyingCode}
        title={`Confirm your account`}
        subtitle={`Enter the code you received at ${
          verificationType === LoginVerificationType.Email ? email : phone
        }.`}
        onGoBack={onGoBack}
        secondaryButton={{
          text:
            verificationType === LoginVerificationType.Email
              ? `Resend email ${
                  secondsRemainingToAllowCodeResend > 0
                    ? `in ${secondsRemainingToAllowCodeResend}s`
                    : ""
                }`
              : `Resend SMS  ${
                  secondsRemainingToAllowCodeResend > 0
                    ? `in ${secondsRemainingToAllowCodeResend}s`
                    : ""
                }`,
          action: () => onSendCode(true),
          isDisabled: secondsRemainingToAllowCodeResend > 0 || isVerifyingCode,
        }}
        showPoweredBy
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="wrap" onClick={handleClick}>
            {CODE_LENGTH.map((v, index) => {
              const selected = values.length === index;
              const filled =
                values.length === CODE_LENGTH.length &&
                index === CODE_LENGTH.length - 1;
              return (
                <div key={index} className="display">
                  {values[index]}
                  {(selected || filled) && focused && (
                    <div className="shadows" />
                  )}
                </div>
              );
            })}
            <input
              value=""
              type="number"
              inputMode="numeric"
              ref={input}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              className="inputOTP"
              style={{
                width: "32px",
                top: "0px",
                bottom: "0px",
                left: `${selectedIndex * 32}px`,
                opacity: hideInput ? 0 : 1,
              }}
            />
          </div>
        </div>
        {!!verifyCodeFeedback && (
          <div className="text-danger">{verifyCodeFeedback}</div>
        )}
      </SessionPanel>
    </>
  );
};

export default VerifyCode;
